// Pure link-resolution helpers. No DOM, no platform APIs.
//
// A markdown `[text](href)` link is either *external* (open in the OS browser)
// or *internal* (resolve to a local file and open it in-app). Authors are
// inconsistent — some links are relative to the current file, others are
// "root-relative" against the workspace root — so we return an ordered list of
// candidate absolute paths to try, most-specific first.

const EXTERNAL_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/**
 * True for links that should open externally rather than as a local file:
 * absolute URLs with a scheme (http:, https:, mailto:, ftp:, etc.),
 * protocol-relative URLs (`//host/...`), and pure in-page anchors (`#id`).
 * Empty/whitespace hrefs are treated as external (nothing to open locally).
 */
export function isExternalLink(href: string): boolean {
  const h = href.trim();
  if (h === '') return true;
  if (h.startsWith('#')) return true; // in-page anchor, not a file
  if (h.startsWith('//')) return true; // protocol-relative
  return EXTERNAL_SCHEME.test(h);
}

export interface LinkResolveOptions {
  /** Absolute path of the document containing the link (for file-relative). */
  docPath?: string;
  /** Absolute path of the workspace root (for root-relative). */
  workspaceRoot?: string;
}

/**
 * Ordered, deduplicated absolute candidate paths for an internal/relative link.
 * Order: (1) resolved against the doc's directory, (2) against the workspace
 * root. Returns [] for external/empty links (see {@link isExternalLink}).
 *
 * The fragment/query are stripped before resolution; paths are normalized
 * (collapsing `.`/`..`). Always emits forward-slash paths.
 */
export function linkCandidates(href: string, opts: LinkResolveOptions = {}): string[] {
  if (isExternalLink(href)) return [];

  const target = stripFragmentAndQuery(href.trim());
  if (target === '') return [];

  const candidates: string[] = [];
  const norm = target.replace(/\\/g, '/');

  if (norm.startsWith('/')) {
    // Root-relative: anchor to the workspace root if known, else use as-is.
    if (opts.workspaceRoot) {
      candidates.push(joinPath(toPosix(opts.workspaceRoot), norm.slice(1)));
    }
    candidates.push(normalizePath(norm));
  } else {
    // File-relative first, then root-relative against the workspace.
    if (opts.docPath) {
      candidates.push(joinPath(dirname(toPosix(opts.docPath)), norm));
    }
    if (opts.workspaceRoot) {
      candidates.push(joinPath(toPosix(opts.workspaceRoot), norm));
    }
  }

  return dedupe(candidates.filter((c) => c !== ''));
}

/**
 * Safety gate for opening an internal link target. Returns true iff the
 * normalized `candidate` equals, or is a strict descendant of, one of the
 * normalized `scopes`. A trailing-separator guard prevents sibling-prefix
 * leaks — `/a/bc` is NOT considered within `/a/b`. Both `/` and `\` separators
 * are normalized to `/` first, so it works for desktop absolute paths and the
 * web's synthetic `webdir/<n>/<root>/…` ids alike.
 *
 * Used by the host to refuse opening any file outside (a) an opened workspace
 * root, or (b) the active document's directory — blocking `..` escapes and
 * absolute paths pointing elsewhere.
 */
export function isWithinScopes(candidate: string, scopes: string[]): boolean {
  const c = normalizeForScope(candidate);
  if (c === '') return false;
  for (const scope of scopes) {
    const s = normalizeForScope(scope);
    if (s === '') continue;
    if (c === s) return true;
    // Descendant: candidate starts with `scope` followed by a separator. The
    // explicit `/` boundary stops `/a/bc` from matching the scope `/a/b`.
    if (c.startsWith(`${s}/`)) return true;
  }
  return false;
}

/** Normalize a path for scope comparison: posix separators, no trailing slash. */
function normalizeForScope(p: string): string {
  const posix = normalizePath(p.replace(/\\/g, '/'));
  // normalizePath already drops a trailing slash for non-root paths; guard the
  // bare-root `/` (and empty) cases explicitly so they compare cleanly.
  return posix === '/' ? '/' : posix.replace(/\/+$/, '');
}

function stripFragmentAndQuery(href: string): string {
  const hash = href.indexOf('#');
  const withoutHash = hash >= 0 ? href.slice(0, hash) : href;
  const q = withoutHash.indexOf('?');
  return q >= 0 ? withoutHash.slice(0, q) : withoutHash;
}

function toPosix(p: string): string {
  return p.replace(/\\/g, '/');
}

function dirname(posixPath: string): string {
  const idx = posixPath.lastIndexOf('/');
  if (idx < 0) return '';
  if (idx === 0) return '/';
  return posixPath.slice(0, idx);
}

/** Join a base directory with a relative segment, then normalize. */
function joinPath(base: string, rel: string): string {
  const joined = base === '' ? rel : `${base.replace(/\/+$/, '')}/${rel}`;
  return normalizePath(joined);
}

/** Collapse `.`/`..` segments and redundant slashes; preserve leading `/`. */
function normalizePath(p: string): string {
  const isAbsolute = p.startsWith('/');
  const out: string[] = [];
  for (const seg of p.split('/')) {
    if (seg === '' || seg === '.') continue;
    if (seg === '..') {
      if (out.length > 0 && out[out.length - 1] !== '..') {
        out.pop();
      } else if (!isAbsolute) {
        out.push('..');
      }
      // For absolute paths, `..` above root is dropped.
      continue;
    }
    out.push(seg);
  }
  const body = out.join('/');
  return isAbsolute ? `/${body}` : body;
}

function dedupe(items: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of items) {
    if (!seen.has(item)) {
      seen.add(item);
      out.push(item);
    }
  }
  return out;
}
